import React, { useState } from 'react';

const PlaceholderLogo = ({ name }) => (
  <div style={{
    width: '100%',
    height: '30px',
    backgroundColor: '#f0f0f0',
    borderRadius: '5px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '12px',
    color: '#000'
  }}>
    {name}
  </div>
);


const NPSVisualization = ({ data, logoMap }) => {
  const [selectedBrand, setSelectedBrand] = useState(0);

  // Process the data to extract NPS components
  const processNPSData = (brand) => {
    const npsData = {
      name: brand.name,
      color: brand.color,
      logo: brand.logo,
      nps: 0,
      promoters: 0,
      detractors: 0,
      passives: 0,
      base: 0
    };

    brand.funnelData.forEach(item => {
      if (item.label === "NPS") {
        npsData.nps = item.percentage;
      } else if (item.label === "Promoters") {
        npsData.promoters = item.percentage;
      } else if (item.label === "Detractor") {
        npsData.detractors = item.percentage;
        // Calculate passives (100% - promoters% - detractors%)
        npsData.passives = 100 - item.percentage - npsData.promoters;
      }
      
      if (item.Base) {
        npsData.base = item.Base;
      }
    });

    return npsData;
  };

  const npsData = data.map(processNPSData);

  return (
    <div style={{
      padding: '24px',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif",
      maxWidth: '1200px',
      margin: '0 auto',
      backgroundColor: '#f8fafc',
      minHeight: '100vh'
    }}>


      {/* Brand Selection */}
      <div style={{
        backgroundColor: 'white',
        padding: '24px',
        borderRadius: '16px',
        marginBottom: '24px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        border: '1px solid #e2e8f0'
      }}>
        <h3 style={{ 
          color: '#1e293b', 
          margin: '0 0 16px 0', 
          fontSize: '18px',
          fontWeight: '600'
        }}>Select Brand:</h3>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
          {npsData.map((brand, index) => (
            <button
              key={index}
              onClick={() => setSelectedBrand(index)}
              style={{
                padding: '12px 20px',
                borderRadius: '8px',
                border: 'none',
                backgroundColor: index === selectedBrand ? brand.color : '#f1f5f9',
                color: index === selectedBrand ? 'white' : '#64748b',
                cursor: 'pointer',
                fontWeight: '500',
                transition: 'all 0.2s ease',
                fontSize: '14px',
                minWidth: '120px',
                boxShadow: index === selectedBrand ? '0 4px 6px rgba(0,0,0,0.1)' : 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = index === selectedBrand ? brand.color : '#e2e8f0';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = index === selectedBrand ? brand.color : '#f1f5f9';
              }}
            >
              
              {brand.name}
            </button>
          ))}
        </div>
      </div>

      {/* NPS Score Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
        gap: '24px',
        marginBottom: '24px'
      }}>
        {npsData.map((brand, index) => (
          <div key={index} style={{
            backgroundColor: 'white',
            padding: '24px',
            borderRadius: '16px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
            border: '1px solid #e2e8f0',
            textAlign: 'center'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '12px',
              marginBottom: '16px'
            }}>
              {logoMap[brand.logo] || <PlaceholderLogo name={brand.name} />}
              <h3 style={{
                color: '#1e293b',
                margin: '0',
                fontSize: '18px',
                fontWeight: '600'
              }}>
                {brand.name}
              </h3>
            </div>
            
            <div style={{
              fontSize: '14px',
              color: '#64748b',
              marginBottom: '20px'
            }}>
              Base: n={brand.base}
            </div>
            
            <div style={{
              fontSize: '42px',
              fontWeight: '700',
              color: brand.nps >= 0 ? (brand.nps >= 50 ? '#16a34a' : '#2563eb') : '#dc2626',
              marginBottom: '16px'
            }}>
              {brand.nps > 0 ? '+' : ''}{brand.nps.toFixed(0)}
            </div>
            
            <div style={{
              fontSize: '16px',
              color: '#64748b',
              fontWeight: '500'
            }}>
              NPS Score
            </div>
            
            <div style={{
              height: '8px',
              backgroundColor: '#e2e8f0',
              borderRadius: '4px',
              margin: '20px 0',
              overflow: 'hidden'
            }}>
              <div style={{
                height: '100%',
                width: `${(brand.nps + 100) / 2}%`,
                background: `linear-gradient(90deg, ${brand.nps >= 0 ? '#2563eb' : '#dc2626'} 0%, ${brand.color} 100%)`,
                borderRadius: '4px'
              }} />
            </div>
            
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: '12px',
              color: '#64748b'
            }}>
              <span>-100</span>
              <span>0</span>
              <span>+100</span>
            </div>
          </div>
        ))}
      </div>

      {/* Detailed NPS Visualization */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '16px',
        padding: '32px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        marginBottom: '30px',
        border: '1px solid #e2e8f0'
      }}>
        <h2 style={{
          color: '#1e293b',
          margin: '0 0 24px 0',
          fontSize: '20px',
          fontWeight: '600',
          display: 'flex',
          alignItems: 'center',
          gap: '10px'
        }}>
          <div style={{
            width: '16px',
            height: '16px',
            backgroundColor: npsData[selectedBrand].color,
            borderRadius: '4px'
          }}></div>
          {npsData[selectedBrand].name} - NPS Breakdown
        </h2>

        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 2fr',
          gap: '32px',
          alignItems: 'center'
        }}>
          {/* NPS Gauge */}
          <div style={{
            textAlign: 'center',
            position: 'relative'
          }}>
            <div style={{
              width: '200px',
              height: '100px',
              margin: '0 auto',
              borderRadius: '100px 100px 0 0',
              overflow: 'hidden',
              position: 'relative',
              background: 'conic-gradient(#dc2626 0deg, #f59e0b 90deg, #84cc16 180deg, #16a34a 270deg)'
            }}>
              <div style={{
                position: 'absolute',
                bottom: '0',
                left: '50%',
                transform: 'translateX(-50%)',
                width: '4px',
                height: '100px',
                backgroundColor: '#1e293b',
                transformOrigin: 'bottom center',
                transform: `translateX(-50%) rotate(${((npsData[selectedBrand].nps + 100) / 200 * 180) - 90}deg)`,
                zIndex: 2
              }} />
              
              <div style={{
                position: 'absolute',
                bottom: '0',
                left: '0',
                right: '0',
                height: '50px',
                backgroundColor: 'white',
                borderRadius: '100px 100px 0 0'
              }} />
            </div>
            
            <div style={{
              fontSize: '14px',
              color: '#64748b',
              marginTop: '16px'
            }}>
              NPS Range: -100 to +100
            </div>
            
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginTop: '8px',
              fontSize: '12px',
              color: '#64748b'
            }}>
              <span>Detractors</span>
              <span>Passives</span>
              <span>Promoters</span>
            </div>
          </div>

          {/* Distribution Bars */}
          <div>
            <div style={{ marginBottom: '24px' }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '8px'
              }}>
                <span style={{ fontSize: '14px', fontWeight: '500', color: '#16a34a' }}>
                  Promoters ({npsData[selectedBrand].promoters.toFixed(1)}%)
                </span>
                <span style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b' }}>
                  {npsData[selectedBrand].promoters.toFixed(1)}%
                </span>
              </div>
              <div style={{
                height: '24px',
                backgroundColor: '#e2e8f0',
                borderRadius: '4px',
                overflow: 'hidden'
              }}>
                <div style={{
                  height: '100%',
                  width: `${npsData[selectedBrand].promoters}%`,
                  backgroundColor: '#16a34a',
                  borderRadius: '4px',
                  transition: 'width 0.5s ease'
                }} />
              </div>
            </div>
            
            <div style={{ marginBottom: '24px' }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '8px'
              }}>
                <span style={{ fontSize: '14px', fontWeight: '500', color: '#f59e0b' }}>
                  Passives ({npsData[selectedBrand].passives.toFixed(1)}%)
                </span>
                <span style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b' }}>
                  {npsData[selectedBrand].passives.toFixed(1)}%
                </span>
              </div>
              <div style={{
                height: '24px',
                backgroundColor: '#e2e8f0',
                borderRadius: '4px',
                overflow: 'hidden'
              }}>
                <div style={{
                  height: '100%',
                  width: `${npsData[selectedBrand].passives}%`,
                  backgroundColor: '#f59e0b',
                  borderRadius: '4px',
                  transition: 'width 0.5s ease'
                }} />
              </div>
            </div>
            
            <div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '8px'
              }}>
                <span style={{ fontSize: '14px', fontWeight: '500', color: '#dc2626' }}>
                  Detractors ({npsData[selectedBrand].detractors.toFixed(1)}%)
                </span>
                <span style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b' }}>
                  {npsData[selectedBrand].detractors.toFixed(1)}%
                </span>
              </div>
              <div style={{
                height: '24px',
                backgroundColor: '#e2e8f0',
                borderRadius: '4px',
                overflow: 'hidden'
              }}>
                <div style={{
                  height: '100%',
                  width: `${npsData[selectedBrand].detractors}%`,
                  backgroundColor: '#dc2626',
                  borderRadius: '4px',
                  transition: 'width 0.5s ease'
                }} />
              </div>
            </div>
          </div>
        </div>
      </div>

      
    </div>
  );
};

export default NPSVisualization;